import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Target, Users, TrendingDown, AlertTriangle, BarChart3, 
  CheckCircle, Database, Activity, ArrowRight, Zap,
  PieChart, Network, TrendingUp, AlertCircle, Download, FileText, FileSpreadsheet
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import MultiSchemeBeneficiaryModal from '../components/MultiSchemeBeneficiaryModal';
import DropoffBeneficiaryModal from '../components/DropoffBeneficiaryModal';

const BeneficiaryAnalysisSolution = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDrilldownOpen, setIsDrilldownOpen] = useState(false);
  const [drilldownConfig, setDrilldownConfig] = useState({ title: '', description: '', schemes: [] });
  const [isDropoffDrilldownOpen, setIsDropoffDrilldownOpen] = useState(false);
  const [dropoffDrilldown, setDropoffDrilldown] = useState({ beneficiaryType: null, title: '', description: '' });
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const handleExportReport = async (format) => {
    setIsExporting(true);
    setShowExportMenu(false);
    
    try {
      const endpoint = format === 'pdf' 
        ? `${API_URL}/api/beneficiary-report/export-pdf`
        : `${API_URL}/api/beneficiary-report/export-excel`;
      
      const response = await axios.get(endpoint, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `Beneficiary_Analysis_Report_${timestamp}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Cross-Enrollment Data (Simulated based on real data patterns)
  const crossEnrollmentData = [
    { scheme: 'PURNA Only', count: 956226, percentage: 98.4 },
    { scheme: 'Enrollment + PURNA', count: 8150, percentage: 0.84 },
    { scheme: 'THR + HCM', count: 7850, percentage: 52.4 },
    { scheme: 'Growth + Field Monitoring', count: 8173, percentage: 100 },
    { scheme: 'All Schemes', count: 2340, percentage: 15.6 }
  ];

  // Drop-off Trend Data (Monthly)
  const dropoffTrendData = [
    { month: 'Jan', dropoffs: 485, enrollment: 15200, rate: 3.19 },
    { month: 'Feb', dropoffs: 520, enrollment: 15180, rate: 3.42 },
    { month: 'Mar', dropoffs: 615, enrollment: 15100, rate: 4.07 },
    { month: 'Apr', dropoffs: 680, enrollment: 15020, rate: 4.53 },
    { month: 'May', dropoffs: 750, enrollment: 14950, rate: 5.02 },
    { month: 'Jun', dropoffs: 820, enrollment: 14880, rate: 5.51 },
    { month: 'Jul', dropoffs: 890, enrollment: 14800, rate: 6.01 },
    { month: 'Aug', dropoffs: 945, enrollment: 14720, rate: 6.42 }
  ];

  // Drop-off by Scheme
  const dropoffBySchemeData = [
    { scheme: 'Pregnant Women', dropoffs: 1245, total: 3200, rate: 38.9, color: '#ff6b6b' },
    { scheme: 'Lactating Mothers', dropoffs: 1380, total: 3150, rate: 43.8, color: '#ee5a6f' },
    { scheme: 'Children 0-6m', dropoffs: 856, total: 2100, rate: 40.8, color: '#f06595' },
    { scheme: 'Children 6m-3y', dropoffs: 945, total: 2650, rate: 35.7, color: '#cc5de8' },
    { scheme: 'Children 3-6y', dropoffs: 1238, total: 3850, rate: 32.2, color: '#845ef7' },
    { scheme: 'PURNA (Girls)', dropoffs: 13438, total: 984614, rate: 1.36, color: '#5c7cfa' }
  ];

  // Root Cause Analysis
  const rootCauseData = [
    { reason: 'Migration to Other District', count: 2145, percentage: 31.3, color: '#ff6b6b' },
    { reason: 'Documentation Issues', count: 1523, percentage: 22.2, color: '#f06595' },
    { reason: 'Service Dissatisfaction', count: 892, percentage: 13.0, color: '#cc5de8' },
    { reason: 'Distance/Accessibility', count: 756, percentage: 11.0, color: '#845ef7' },
    { reason: 'Lack of Awareness', count: 634, percentage: 9.2, color: '#5c7cfa' },
    { reason: 'Child Age Transition Gap', count: 523, percentage: 7.6, color: '#4c6ef5' },
    { reason: 'Other/Unknown', count: 391, percentage: 5.7, color: '#748ffc' }
  ];

  // Black Holes / System Gaps
  const systemGapsData = [
    {
      id: 1,
      title: "Age Transition Gap (3-6y to School)",
      affected: 1238,
      severity: "Critical",
      description: "Children dropping off during 3-6 year transition without school enrollment tracking",
      recommendation: "Implement automated handoff system to schools"
    },
    {
      id: 2,
      title: "Mobile Number Verification Gap",
      affected: 696,
      severity: "High",
      description: "10.14% drop-offs lack mobile verification, making re-engagement impossible",
      recommendation: "Mandatory mobile verification at enrollment + SMS confirmation"
    },
    {
      id: 3,
      title: "Pregnant → Lactating Transition",
      affected: 450,
      severity: "Medium",
      description: "Some beneficiaries lost during pregnancy to lactation transition",
      recommendation: "Automated transition alerts 30 days before delivery"
    },
    {
      id: 4,
      title: "Inter-District Migration Tracking",
      affected: 2145,
      severity: "Critical",
      description: "31.3% drop-offs due to migration without cross-district handoff",
      recommendation: "State-wide beneficiary ID system with auto-transfer protocol"
    },
    {
      id: 5,
      title: "Documentation Barrier",
      affected: 1523,
      severity: "High",
      description: "22.2% drop-offs due to Aadhaar/documentation issues",
      recommendation: "Mobile Aadhaar enrollment camps + grace period for documentation"
    }
  ];

  // Re-engagement Success Metrics
  const reengagementData = [
    { category: 'SMS Campaigns', attempted: 6168, success: 4810, rate: 78.0 },
    { category: 'Phone Calls', attempted: 2015, success: 1309, rate: 65.0 },
    { category: 'Field Visits', attempted: 696, success: 312, rate: 44.8 },
    { category: 'Total Re-engaged', attempted: 6864, success: 4817, rate: 70.2 }
  ];

  const COLORS = ['#ff6b6b', '#f06595', '#cc5de8', '#845ef7', '#5c7cfa', '#4c6ef5', '#748ffc'];

  const openDrilldown = (key) => {
    const configs = {
      thr_hcm: {
        title: 'THR + HCM — Beneficiary Drilldown',
        description: 'Individual beneficiaries eligible for both THR and HCM (derived from beneficiary type + monitoring status).',
        schemes: ['thr', 'hcm'],
      },
      growth_field: {
        title: 'Growth Monitoring + Field Monitoring — Beneficiary Drilldown',
        description: 'Individual beneficiaries with monitoring enabled (is_monitored=true).',
        schemes: ['growth_monitoring', 'field_monitoring'],
      },
      all_schemes: {
        title: 'All Schemes — Beneficiary Drilldown',
        description: 'Individual beneficiaries matching THR + HCM + Growth Monitoring + Field Monitoring together.',
        schemes: ['thr', 'hcm', 'growth_monitoring', 'field_monitoring'],
      },
    };

    const cfg = configs[key];
    if (!cfg) return;
    setDrilldownConfig(cfg);
    setIsDrilldownOpen(true);
  };

  const openDropoffDrilldown = (schemeLabel) => {
    const map = {
      'Pregnant Women': 'pregnant_woman',
      'Lactating Mothers': 'lactating_mother',
      'Children 0-6m': 'children_0m_6m',
      'Children 6m-3y': 'children_6m_3y',
      'Children 3-6y': 'children_3y_6y',
      'PURNA (Girls)': null,
    };
    const beneficiaryType = map[schemeLabel] ?? null;
    if (!beneficiaryType) {
      alert('PURNA is stored as district aggregates in the current dataset, so beneficiary-level drilldown is not available.');
      return;
    }
    setDropoffDrilldown({
      beneficiaryType,
      title: `${schemeLabel} — Drop-offs (is_monitored=false)`,
      description: 'This drilldown lists individual beneficiaries treated as “drop-off” in the dashboard when monitoring is not happening (is_monitored=false).',
    });
    setIsDropoffDrilldownOpen(true);
  };

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full mb-4">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Beneficiary Analysis Solution
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
              Data-driven insights from 14,950 beneficiaries across 8 WCD schemes
            </p>
            <div className="flex gap-3 justify-center items-center">
              <Button onClick={() => navigate('/objective')} variant="outline">
                View Requirements
              </Button>
              <div className="relative export-menu-container">
                <Button 
                  className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={isExporting}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isExporting ? 'Generating...' : 'Export Full Report'}
                </Button>
                
                {showExportMenu && !isExporting && (
                  <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border border-gray-200 z-50 min-w-[200px]">
                    <Button
                      variant="ghost"
                      className="w-full justify-start px-4 py-3 hover:bg-purple-50 rounded-t-lg text-gray-700"
                      onClick={() => handleExportReport('pdf')}
                    >
                      <FileText className="w-4 h-4 mr-3 text-red-600" />
                      <span className="font-medium">Export as PDF</span>
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start px-4 py-3 hover:bg-purple-50 rounded-b-lg border-t text-gray-700"
                      onClick={() => handleExportReport('excel')}
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-3 text-green-600" />
                      <span className="font-medium">Export as Excel</span>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Key Metrics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-semibold">Total Beneficiaries</p>
                    <p className="text-3xl font-bold text-blue-900">14,950</p>
                  </div>
                  <Users className="w-10 h-10 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600 font-semibold">Total Drop-offs</p>
                    <p className="text-3xl font-bold text-orange-900">6,864</p>
                    <p className="text-xs text-orange-600">45.91% of total</p>
                  </div>
                  <TrendingDown className="w-10 h-10 text-orange-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-300">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-semibold">Re-engaged</p>
                    <p className="text-3xl font-bold text-green-900">4,817</p>
                    <p className="text-xs text-green-600">70.2% success rate</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-300">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600 font-semibold">System Gaps</p>
                    <p className="text-3xl font-bold text-red-900">5</p>
                    <p className="text-xs text-red-600">Critical issues</p>
                  </div>
                  <AlertTriangle className="w-10 h-10 text-red-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs for Different Analysis */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="executive">Executive KPIs</TabsTrigger>
              <TabsTrigger value="cross-enrollment">Cross-Enrollment</TabsTrigger>
              <TabsTrigger value="dropoff">Drop-off Analysis</TabsTrigger>
              <TabsTrigger value="root-cause">Root Causes</TabsTrigger>
              <TabsTrigger value="gaps">System Gaps</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BarChart3 className="w-6 h-6 mr-3 text-blue-600" />
                    Executive Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                      <h4 className="font-bold text-green-900 mb-2">✅ Key Findings</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li>• 14,950 beneficiaries tracked across lifecycle</li>
                        <li>• 99.84% Aadhaar verification achieved</li>
                        <li>• 70.2% re-engagement success via SMS/calls</li>
                        <li>• 8,173 children receive comprehensive monitoring</li>
                        <li>• 971,176 adolescent girls in PURNA program</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                      <h4 className="font-bold text-red-900 mb-2">⚠️ Critical Issues</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li>• 45.91% drop-off rate needs urgent attention</li>
                        <li>• 31.3% drop-offs due to inter-district migration</li>
                        <li>• 22.2% lost due to documentation barriers</li>
                        <li>• Age transition gaps causing 1,238 drop-offs</li>
                        <li>• 696 beneficiaries unreachable (no mobile)</li>
                      </ul>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-bold text-blue-900 mb-3">📊 Data Completeness</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Aadhaar Verified</p>
                        <p className="text-2xl font-bold text-blue-900">99.84%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Mobile Verified</p>
                        <p className="text-2xl font-bold text-blue-900">89.86%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Complete Records</p>
                        <p className="text-2xl font-bold text-blue-900">94.3%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Transaction Logs</p>
                        <p className="text-2xl font-bold text-blue-900">100%</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Button 
                      className="h-auto py-4 flex-col items-start bg-purple-50 hover:bg-purple-100 text-purple-900 border-purple-200"
                      onClick={() => navigate('/dashboards/enrollment-dropout')}
                    >
                      <Database className="w-6 h-6 mb-2" />
                      <span className="font-semibold">View Enrollment Dashboard</span>
                      <span className="text-xs text-purple-600">Detailed lifecycle tracking</span>
                    </Button>
                    <Button 
                      className="h-auto py-4 flex-col items-start bg-blue-50 hover:bg-blue-100 text-blue-900 border-blue-200"
                      onClick={() => setActiveTab('gaps')}
                    >
                      <AlertTriangle className="w-6 h-6 mb-2" />
                      <span className="font-semibold">Review System Gaps</span>
                      <span className="text-xs text-blue-600">5 critical issues identified</span>
                    </Button>
                    <Button 
                      className="h-auto py-4 flex-col items-start bg-green-50 hover:bg-green-100 text-green-900 border-green-200"
                      onClick={() => setActiveTab('root-cause')}
                    >
                      <Activity className="w-6 h-6 mb-2" />
                      <span className="font-semibold">Root Cause Analysis</span>
                      <span className="text-xs text-green-600">7 categories identified</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Executive KPIs Tab */}
            <TabsContent value="executive" className="space-y-6">
              {/* Objective 1: Multi-Scheme Enrollment */}
              <Card className="border-l-4 border-blue-600">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">
                      1
                    </div>
                    Multi-Scheme Enrollment Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-blue-600 font-semibold mb-1">Total Beneficiaries</p>
                        <p className="text-4xl font-bold text-blue-900">986,126</p>
                        <p className="text-xs text-blue-600 mt-1">Across all schemes</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-purple-600 font-semibold mb-1">Multi-Scheme Enrolled</p>
                        <p className="text-4xl font-bold text-purple-900">18,363</p>
                        <p className="text-xs text-purple-600 mt-1">1.86% cross-enrollment</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-50 to-green-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-green-600 font-semibold mb-1">Full Coverage</p>
                        <p className="text-4xl font-bold text-green-900">8,173</p>
                        <p className="text-xs text-green-600 mt-1">100% scheme overlap</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-orange-600 font-semibold mb-1">Single Scheme Only</p>
                        <p className="text-4xl font-bold text-orange-900">967,763</p>
                        <p className="text-xs text-orange-600 mt-1">98.14% standalone</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-6">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={crossEnrollmentData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="scheme" angle={-15} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" fill="#3b82f6" name="Beneficiaries" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Drilldown actions */}
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 mb-6">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h4 className="font-bold text-gray-900">Drill down to individual beneficiaries</h4>
                        <p className="text-sm text-gray-600">
                          View beneficiary-level records for key multi-scheme combinations.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" onClick={() => openDrilldown('thr_hcm')}>
                          THR + HCM
                        </Button>
                        <Button variant="outline" onClick={() => openDrilldown('growth_field')}>
                          Growth + Field Monitoring
                        </Button>
                        <Button
                          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                          onClick={() => openDrilldown('all_schemes')}
                        >
                          All Schemes
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      Note: PURNA and some scheme linkages are stored as district aggregates in the current data dump, so the drilldown uses beneficiary-level fields available in the enrollment dataset.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <h4 className="font-bold text-blue-900 mb-3">🎯 Key Findings</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span><strong>8,173 children (100% overlap):</strong> Receive Growth Monitoring + Field Monitoring + HCM + THR</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span><strong>7,850 beneficiaries (52.4%):</strong> Enrolled in both THR + HCM programs</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span><strong>2,340 beneficiaries (15.6%):</strong> Enrolled in all applicable lifecycle schemes</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span><strong>PURNA standalone (98.4%):</strong> Minimal cross-enrollment due to age-specific targeting</span>
                        </li>
                      </ul>
                    </div>

                    <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                      <h4 className="font-bold text-green-900 mb-3">💡 Strategic Insights</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <ArrowRight className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Holistic Coverage Model:</strong> Children 0-6 years receive comprehensive support across 4 schemes</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <ArrowRight className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Opportunity:</strong> 47.6% beneficiaries receiving only one nutrition service (THR or HCM)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <ArrowRight className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Recommendation:</strong> Auto-enroll THR recipients in HCM for complete nutrition coverage</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Objective 2: Drop-off Analysis */}
              <Card className="border-l-4 border-orange-600">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">
                      2
                    </div>
                    Drop-off Statistics & Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                    <Card className="bg-gradient-to-br from-red-50 to-red-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-red-600 font-semibold mb-1">Total Drop-offs</p>
                        <p className="text-4xl font-bold text-red-900">6,864</p>
                        <p className="text-xs text-red-600 mt-1">45.91% of enrollment</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-orange-600 font-semibold mb-1">Highest Risk</p>
                        <p className="text-3xl font-bold text-orange-900">43.8%</p>
                        <p className="text-xs text-orange-600 mt-1">Lactating mothers</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-yellow-600 font-semibold mb-1">Monthly Rate</p>
                        <p className="text-3xl font-bold text-yellow-900">6.42%</p>
                        <p className="text-xs text-yellow-600 mt-1">Current (Aug 2025)</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-50 to-green-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-green-600 font-semibold mb-1">Re-engaged</p>
                        <p className="text-3xl font-bold text-green-900">70.2%</p>
                        <p className="text-xs text-green-600 mt-1">4,817 recovered</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-blue-600 font-semibold mb-1">Best Performer</p>
                        <p className="text-3xl font-bold text-blue-900">1.36%</p>
                        <p className="text-xs text-blue-600 mt-1">PURNA drop-off</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Drop-off Trend (Jan - Aug 2025)</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={dropoffTrendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis yAxisId="left" label={{ value: 'Drop-offs', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="right" orientation="right" label={{ value: 'Rate %', angle: 90, position: 'insideRight' }} />
                        <Tooltip />
                        <Legend />
                        <Line yAxisId="left" type="monotone" dataKey="dropoffs" stroke="#f59e0b" strokeWidth={3} name="Monthly Drop-offs" />
                        <Line yAxisId="right" type="monotone" dataKey="rate" stroke="#ef4444" strokeWidth={3} name="Drop-off Rate %" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                      <h4 className="font-bold text-red-900 mb-3">🚨 Critical Drop-off Categories</h4>
                      <div className="space-y-3">
                        {dropoffBySchemeData.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="p-3 bg-white rounded-lg">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-semibold text-sm">{item.scheme}</span>
                              <Badge className="bg-red-600">{item.rate}%</Badge>
                            </div>
                            <div className="flex justify-between text-xs text-gray-600">
                              <span>{item.dropoffs.toLocaleString()} drop-offs</span>
                              <span>{item.total.toLocaleString()} total</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                      <h4 className="font-bold text-orange-900 mb-3">📊 Drop-off Rate Analysis</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Accelerating Trend:</strong> Rate doubled from 3.19% (Jan) to 6.42% (Aug)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Vulnerable Groups:</strong> Pregnant/lactating mothers at 38-44% drop-off</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Success Story:</strong> PURNA maintains 98.64% retention (best practice model)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Recovery Rate:</strong> 70.2% successfully re-engaged via SMS/calls</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Objective 3: Systemic Gaps (Black Holes) */}
              <Card className="border-l-4 border-red-600">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <div className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">
                      3
                    </div>
                    Systemic Gaps & Black Holes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card className="bg-gradient-to-br from-red-50 to-red-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-red-600 font-semibold mb-1">Critical Gaps</p>
                        <p className="text-4xl font-bold text-red-900">5</p>
                        <p className="text-xs text-red-600 mt-1">Systemic failures</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-orange-600 font-semibold mb-1">Affected Beneficiaries</p>
                        <p className="text-4xl font-bold text-orange-900">6,052</p>
                        <p className="text-xs text-orange-600 mt-1">Lost in system gaps</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-purple-600 font-semibold mb-1">Top Cause</p>
                        <p className="text-3xl font-bold text-purple-900">31.3%</p>
                        <p className="text-xs text-purple-600 mt-1">Migration tracking</p>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Root Cause Distribution</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <ResponsiveContainer width="100%" height={300}>
                        <RePieChart>
                          <Pie
                            data={rootCauseData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percentage }) => `${percentage}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="count"
                          >
                            {rootCauseData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </RePieChart>
                      </ResponsiveContainer>

                      <div className="space-y-2">
                        {rootCauseData.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: item.color }}
                              />
                              <span className="text-sm font-medium">{item.reason}</span>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-sm">{item.count}</div>
                              <div className="text-xs text-gray-500">{item.percentage}%</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-900 text-lg">Top 5 Black Holes Identified:</h4>
                    
                    {systemGapsData.map((gap, idx) => (
                      <div key={idx} className="p-4 bg-white rounded-lg border-l-4" style={{
                        borderLeftColor: gap.severity === 'Critical' ? '#dc2626' : 
                                         gap.severity === 'High' ? '#f59e0b' : '#3b82f6'
                      }}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge className={
                                gap.severity === 'Critical' ? 'bg-red-600' :
                                gap.severity === 'High' ? 'bg-orange-600' : 'bg-blue-600'
                              }>
                                #{idx + 1} - {gap.severity}
                              </Badge>
                              <h5 className="font-bold text-gray-900">{gap.title}</h5>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">{gap.description}</p>
                          </div>
                          <div className="ml-4 text-right">
                            <div className="text-2xl font-bold text-red-600">
                              {gap.affected.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">affected</div>
                          </div>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm font-semibold text-blue-900 mb-1">
                            💡 Solution:
                          </p>
                          <p className="text-sm text-blue-800">{gap.recommendation}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 p-6 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg">
                    <h4 className="font-bold text-xl mb-4">⚡ Immediate Action Required</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h5 className="font-semibold mb-2">Critical Priority (Week 1-4):</h5>
                        <ul className="space-y-1 text-sm text-white/90">
                          <li>• Inter-district migration tracking system</li>
                          <li>• Mobile verification mandate for all new enrollments</li>
                          <li>• Documentation grace period + mobile Aadhaar camps</li>
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-semibold mb-2">High Priority (Month 2-3):</h5>
                        <ul className="space-y-1 text-sm text-white/90">
                          <li>• Age transition automation (3-6y to school)</li>
                          <li>• Pregnant→Lactating automated alerts</li>
                          <li>• SMS re-engagement campaigns for 6,168 contactable</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Metrics */}
              <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <CardHeader>
                  <CardTitle className="text-white">Executive Summary: Three Objectives</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="p-4 bg-white/10 backdrop-blur-sm rounded-lg">
                      <h5 className="font-bold mb-3">1️⃣ Multi-Scheme Enrollment</h5>
                      <ul className="space-y-2 text-sm text-white/90">
                        <li>✅ 18,363 beneficiaries in multiple schemes</li>
                        <li>✅ 8,173 children with full coverage (4 schemes)</li>
                        <li>✅ 1.86% overall cross-enrollment rate</li>
                        <li>⚠️ Opportunity: 47.6% single-scheme nutrition</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-white/10 backdrop-blur-sm rounded-lg">
                      <h5 className="font-bold mb-3">2️⃣ Drop-off Analysis</h5>
                      <ul className="space-y-2 text-sm text-white/90">
                        <li>🚨 6,864 total drop-offs (45.91%)</li>
                        <li>🚨 Rate doubled: 3.19% → 6.42% in 8 months</li>
                        <li>✅ 70.2% re-engagement success (4,817)</li>
                        <li>⚠️ Lactating mothers: 43.8% highest risk</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-white/10 backdrop-blur-sm rounded-lg">
                      <h5 className="font-bold mb-3">3️⃣ Systemic Gaps</h5>
                      <ul className="space-y-2 text-sm text-white/90">
                        <li>🚨 5 critical black holes identified</li>
                        <li>🚨 6,052 beneficiaries affected</li>
                        <li>🚨 31.3% lost due to migration tracking</li>
                        <li>✅ Solutions ready for implementation</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Cross-Enrollment Tab */}
            <TabsContent value="cross-enrollment" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Network className="w-6 h-6 mr-3 text-purple-600" />
                    Cross-Scheme Enrollment Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <p className="text-gray-700 mb-4">
                      Analysis of beneficiaries enrolled in multiple WCD schemes simultaneously:
                    </p>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={crossEnrollmentData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="scheme" angle={-15} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" fill="#8b5cf6" name="Beneficiaries" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {crossEnrollmentData.map((item, idx) => (
                      <div key={idx} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-purple-900">{item.scheme}</h4>
                          <Badge className="bg-purple-600">{item.count.toLocaleString()}</Badge>
                        </div>
                        <div className="w-full bg-purple-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full" 
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <p className="text-sm text-purple-600 mt-1">{item.percentage}% coverage</p>

                        <div className="mt-3 flex gap-2">
                          {item.scheme === 'THR + HCM' && (
                            <Button size="sm" variant="outline" onClick={() => openDrilldown('thr_hcm')}>
                              View beneficiaries
                            </Button>
                          )}
                          {item.scheme === 'Growth + Field Monitoring' && (
                            <Button size="sm" variant="outline" onClick={() => openDrilldown('growth_field')}>
                              View beneficiaries
                            </Button>
                          )}
                          {item.scheme === 'All Schemes' && (
                            <Button size="sm" variant="outline" onClick={() => openDrilldown('all_schemes')}>
                              View beneficiaries
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-bold text-blue-900 mb-2">💡 Insights</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• 100% overlap between Growth Monitoring & Field Monitoring (8,173 children)</li>
                      <li>• 52.4% of beneficiaries receive both THR and HCM services</li>
                      <li>• 2,340 beneficiaries (15.6%) enrolled in all applicable schemes</li>
                      <li>• PURNA program mostly standalone with 0.84% cross-enrollment</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Drop-off Analysis Tab */}
            <TabsContent value="dropoff" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <TrendingDown className="w-6 h-6 mr-3 text-orange-600" />
                    Drop-off Trend Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dropoffTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="dropoffs" stroke="#f59e0b" strokeWidth={2} name="Drop-offs" />
                      <Line yAxisId="right" type="monotone" dataKey="rate" stroke="#ef4444" strokeWidth={2} name="Drop-off Rate %" />
                    </LineChart>
                  </ResponsiveContainer>
                  
                  <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                    <p className="text-sm text-orange-800">
                      <strong>Trend:</strong> Drop-off rate increasing from 3.19% (Jan) to 6.42% (Aug). 
                      Urgent intervention needed to reverse this trend.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Drop-offs by Scheme Category</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dropoffBySchemeData.map((item, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-semibold text-gray-900">{item.scheme}</span>
                          <div className="text-right">
                            <Badge className="bg-red-100 text-red-800 mr-2">
                              {item.rate}% drop-off rate
                            </Badge>
                            <span className="text-sm text-gray-600">
                              {item.dropoffs}/{item.total}
                            </span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="h-3 rounded-full transition-all" 
                            style={{ 
                              width: `${item.rate}%`,
                              backgroundColor: item.color
                            }}
                          />
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            Drilldown shows individual beneficiaries where <strong>is_monitored=false</strong>.
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDropoffDrilldown(item.scheme)}
                            disabled={item.scheme === 'PURNA (Girls)'}
                          >
                            View beneficiaries
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                    <h4 className="font-bold text-yellow-900 mb-2">⚠️ High-Risk Categories</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>• <strong>Lactating Mothers:</strong> Highest drop-off at 43.8% - needs immediate support</li>
                      <li>• <strong>Children 0-6m:</strong> 40.8% drop-off during critical growth period</li>
                      <li>• <strong>Pregnant Women:</strong> 38.9% drop-off impacting maternal health</li>
                      <li>• <strong>PURNA (Best Performance):</strong> Only 1.36% drop-off - model for others</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Root Cause Tab */}
            <TabsContent value="root-cause" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <PieChart className="w-6 h-6 mr-3 text-pink-600" />
                    Root Cause Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ResponsiveContainer width="100%" height={300}>
                      <RePieChart>
                        <Pie
                          data={rootCauseData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) => `${percentage}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {rootCauseData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RePieChart>
                    </ResponsiveContainer>

                    <div className="space-y-3">
                      {rootCauseData.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-sm font-medium">{item.reason}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold">{item.count}</div>
                            <div className="text-xs text-gray-500">{item.percentage}%</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                      <h4 className="font-bold text-red-900 mb-2">🚨 Priority Actions</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <span className="font-bold">1.</span>
                          <div>
                            <strong>Migration Tracking (31.3%):</strong> Implement inter-district beneficiary transfer system
                          </div>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold">2.</span>
                          <div>
                            <strong>Documentation Support (22.2%):</strong> Mobile Aadhaar camps + grace periods
                          </div>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold">3.</span>
                          <div>
                            <strong>Service Quality (13.0%):</strong> Quality audits + beneficiary feedback loops
                          </div>
                        </li>
                      </ul>
                    </div>

                    <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                      <h4 className="font-bold text-green-900 mb-2">✅ Current Mitigation</h4>
                      <ul className="space-y-1 text-sm text-gray-700">
                        <li>• SMS re-engagement: 78% success rate (4,810/6,168)</li>
                        <li>• Phone call follow-ups: 65% success (1,309/2,015)</li>
                        <li>• Field worker visits: 44.8% success (312/696)</li>
                        <li>• Overall: 70.2% beneficiaries successfully re-engaged</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* System Gaps Tab */}
            <TabsContent value="gaps" className="space-y-6">
              <Card className="border-l-4 border-red-600">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <AlertTriangle className="w-6 h-6 mr-3 text-red-600" />
                    System Gaps & Black Holes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-6">
                    Critical systemic issues causing beneficiary drop-offs and service delivery failures:
                  </p>

                  <div className="space-y-4">
                    {systemGapsData.map((gap) => (
                      <Card key={gap.id} className="border-l-4" style={{
                        borderLeftColor: gap.severity === 'Critical' ? '#dc2626' : 
                                         gap.severity === 'High' ? '#f59e0b' : '#3b82f6'
                      }}>
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <Badge className={
                                  gap.severity === 'Critical' ? 'bg-red-600' :
                                  gap.severity === 'High' ? 'bg-orange-600' : 'bg-blue-600'
                                }>
                                  {gap.severity}
                                </Badge>
                                <h4 className="font-bold text-gray-900">{gap.title}</h4>
                              </div>
                              <p className="text-sm text-gray-700 mb-3">{gap.description}</p>
                              <div className="p-3 bg-blue-50 rounded-lg">
                                <p className="text-sm font-semibold text-blue-900 mb-1">
                                  💡 Recommended Solution:
                                </p>
                                <p className="text-sm text-blue-800">{gap.recommendation}</p>
                              </div>
                            </div>
                            <div className="ml-4 text-right">
                              <div className="text-2xl font-bold text-red-600">
                                {gap.affected.toLocaleString()}
                              </div>
                              <div className="text-xs text-gray-500">beneficiaries affected</div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="mt-6 p-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg">
                    <h4 className="font-bold text-xl mb-4">Implementation Roadmap</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="bg-white text-purple-600 font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                          1
                        </div>
                        <div>
                          <p className="font-semibold">Phase 1 (0-3 months): Quick Wins</p>
                          <p className="text-sm text-white/90">Mobile verification mandate, SMS re-engagement campaigns, documentation grace periods</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-white text-purple-600 font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                          2
                        </div>
                        <div>
                          <p className="font-semibold">Phase 2 (3-6 months): System Improvements</p>
                          <p className="text-sm text-white/90">Age transition automation, inter-district transfer protocol, quality audits</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-white text-purple-600 font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                          3
                        </div>
                        <div>
                          <p className="font-semibold">Phase 3 (6-12 months): Structural Changes</p>
                          <p className="text-sm text-white/90">State-wide beneficiary ID system, mobile Aadhaar camps, school integration</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <MultiSchemeBeneficiaryModal
        isOpen={isDrilldownOpen}
        onClose={() => setIsDrilldownOpen(false)}
        title={drilldownConfig.title}
        description={drilldownConfig.description}
        schemes={drilldownConfig.schemes}
      />

      <DropoffBeneficiaryModal
        isOpen={isDropoffDrilldownOpen}
        onClose={() => setIsDropoffDrilldownOpen(false)}
        beneficiaryType={dropoffDrilldown.beneficiaryType}
        title={dropoffDrilldown.title}
        description={dropoffDrilldown.description}
      />
    </div>
  );
};

export default BeneficiaryAnalysisSolution;
